import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Download, X } from 'lucide-react';
import { useEffect, useRef, useState, type RefObject } from 'react';
import { usePageScroll } from '../hooks/usePageScroll';
import { PdfCanvas, type PdfLayout } from './PdfCanvas';
import { PdfToolbar, ZOOM } from './PdfToolbar';

type Props = {
  url: string;
  title: string;
  subtitle: string;
  /** The page to show: a change from outside jumps there, and scrolling reports back through onPage. */
  page: number;
  passage: string | null;
  figure?: number | null;
  /** The page the passage or figure is on. */
  tintPage: number;
  onPage: (page: number) => void;
};

type ViewProps = Pick<Props, 'url' | 'page' | 'passage' | 'figure' | 'tintPage' | 'onPage'> & {
  zoom: number;
  isFullscreen: boolean;
  /** The element the fit-to-width follows; the full-screen view has none. */
  fitRef?: RefObject<HTMLDivElement | null>;
  onZoom: (zoom: number) => void;
  onFullscreen: () => void;
};

/** The pages scrolling one after another, with the controls floating over them. */
function PdfView({ url, page, passage, figure = null, tintPage, onPage, zoom, isFullscreen, fitRef, onZoom, onFullscreen }: ViewProps) {
  const ownRef = useRef<HTMLDivElement>(null);
  const scrollRef = fitRef ?? ownRef;
  const [layout, setLayout] = useState<PdfLayout>({ pages: 0, ratio: 1 });
  const { current, goTo } = usePageScroll({ scrollRef, page, pages: layout.pages, zoom, onPage });

  return (
    <Box ref={scrollRef} sx={{ position: 'relative', height: '100%', overflow: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', px: 3, pt: 3, pb: 10, minWidth: 'fit-content' }}>
        <PdfCanvas url={url} zoom={zoom} layout={layout} scrollRef={scrollRef} passage={passage} figure={figure} tintPage={tintPage} onLoaded={setLayout} />
      </Box>
      <Box sx={{ position: 'sticky', bottom: 16, display: 'flex', justifyContent: 'center', pointerEvents: 'none', '& > *': { pointerEvents: 'auto' } }}>
        <PdfToolbar
          page={current}
          pages={layout.pages}
          zoom={zoom}
          url={url}
          isFullscreen={isFullscreen}
          onPage={(p) => { goTo(p); onPage(p); }}
          onZoom={onZoom}
          onFullscreen={onFullscreen}
        />
      </Box>
    </Box>
  );
}

/** The report itself beside the panel, and the same view full screen on a dark backdrop. */
export function PdfPane({ url, title, subtitle, page, passage, figure = null, tintPage, onPage }: Props) {
  const fitRef = useRef<HTMLDivElement>(null);
  const fit = useFitWidth(fitRef);
  // Fits the column until the reader zooms; then their zoom holds.
  const [picked, setPicked] = useState<number | null>(null);
  const zoom = picked ?? fit;
  const [isFullscreen, setFullscreen] = useState(false);
  const shared = { url, page, passage, figure, tintPage, onPage, zoom, onZoom: setPicked };

  return (
    <>
      <PdfView {...shared} isFullscreen={false} fitRef={fitRef} onFullscreen={() => { setFullscreen(true); }} />
      <Dialog fullScreen open={isFullscreen} onClose={() => { setFullscreen(false); }} slotProps={{ paper: { sx: { bgcolor: 'grey.900', borderRadius: 0 } } }}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5, color: 'common.white' }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h4" component="h2" noWrap>{title}</Typography>
            <Typography variant="caption" sx={{ color: 'grey.500' }}>{subtitle}</Typography>
          </Box>
          <Button size="small" component="a" href={url} download startIcon={<Download size={14} />} sx={{ color: 'common.white', bgcolor: 'grey.800', '&:hover': { bgcolor: 'grey.700' } }}>
            Download
          </Button>
          <IconButton aria-label="Close full screen" onClick={() => { setFullscreen(false); }} sx={{ color: 'common.white', '&:hover': { bgcolor: 'grey.800' } }}>
            <X size={18} />
          </IconButton>
        </Stack>
        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
          <PdfView {...shared} isFullscreen onFullscreen={() => { setFullscreen(false); }} />
        </Box>
      </Dialog>
    </>
  );
}

const PAGE_WIDTH = 612; // a PDF page at 100%
const GUTTER = 48;
const MAX_FIT = 1.25; // a wide screen should not blow the page up

/** The zoom at which a page fills the column's width, kept up to date as the column resizes. */
function useFitWidth(ref: RefObject<HTMLDivElement | null>) {
  const [fit, setFit] = useState<number>(ZOOM.start);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const z = Math.floor(((entry.contentRect.width - GUTTER) / PAGE_WIDTH) * 20) / 20; // 5% steps
      setFit(Math.min(MAX_FIT, Math.max(ZOOM.min, z)));
    });
    observer.observe(el);
    return () => { observer.disconnect(); };
  }, [ref]);
  return fit;
}
